import { usePropertyContext } from "@app/app/hooks";
import { ControlledFormField } from "@app/common/components/form/ControlledFormField";
import { CountrySelect } from "@app/common/components/form/CountrySelect";
import { Label } from "@app/common/components/form/Label";
import { ICountry } from "@app/common/types";
import { Button } from "@app/componentLibrary/ui/button";
import { Input } from "@app/componentLibrary/ui/form/Input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@app/componentLibrary/ui/select";
import { LoyaltyContext } from "@app/loyalty/context/LoyaltyContext";
import { getDialingCodes, registerBookingWhizzMember } from "@app/loyalty/queries";
import { IBookingWhizzMemberRegisterFormFields } from "@app/loyalty/types";
import { Fragment, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

function checkPasswordValidity(password: string): boolean {
    const isMinLengthValid = password.length >= 8;
    const isUpperCaseValid = /[A-Z]/.test(password);
    const isLowerCaseValid = /[a-z]/.test(password);
    const isDigitValid = /\d/.test(password);
    const isSpecialCharValid = /[@$!%*?&()]/.test(password);

    return isMinLengthValid && isUpperCaseValid && isLowerCaseValid && isDigitValid && isSpecialCharValid;
}

function checkNameValidity(name: string): boolean {
    return !/\d/.test(name) && !/[@$!%*?&()]/.test(name);
}

export const BookingWhizzMemberRegisterForm = () => {
    const { activePropertyId } = usePropertyContext();
    const formMethods = useForm<IBookingWhizzMemberRegisterFormFields>({
        defaultValues: {},
        mode: "onChange",
    });
    const { t } = useTranslation(["vendorLoyalty"]);

    const [isRegisteredSuccessfully, setIsRegisteredSuccessfully] = useState<boolean>(false);
    const [hasRegisterFailed, setHasRegisterFailed] = useState<boolean>(false);
    const [emailValue, setEmailValue] = useState<string>("");
    const [passwordValue, setPasswordValue] = useState<string>("");
    const [titleValue, setTitleValue] = useState<string>("Mr");
    const [firstNameValue, setFirstNameValue] = useState<string>("");
    const [lastNameValue, setLastNameValue] = useState<string>("");
    const [dateOfBirthValue, setDateOfBirthValue] = useState<string>("");
    const [phoneNumberValue, setPhoneNumberValue] = useState<string>("");
    const [mobileCodeValue, setMobileCodeValue] = useState<string>("");
    const [countryCodeValue, setCountryCodeValue] = useState<string>("");
    const [addressValue, setAddressValue] = useState<string>("");
    const [isAbleToSubmit, setIsAbleToSubmit] = useState<boolean>(false);
    const dialingCodes = getDialingCodes();
    const { countries } = useContext(LoyaltyContext);

    const dialingCodesOptions = dialingCodes.map((code) => ({ label: code, value: code }));

    useEffect(() => {
        const isEmailValid = emailValue.trim() !== "";
        const isPasswordValid = checkPasswordValidity(passwordValue);
        const isTitleValid = titleValue.trim() !== "";
        const isFirstNameValid = firstNameValue.trim() !== "";
        const isLastNameValid = lastNameValue.trim() !== "";
        const isDateOfBirthValid = dateOfBirthValue.trim() !== "";
        const isPhoneNumberValid = phoneNumberValue.trim() !== "";
        const isMobileCodeValid = mobileCodeValue.trim() !== "";
        const isAddressValid = addressValue.trim() !== "";

        setIsAbleToSubmit(
            isEmailValid &&
                isPasswordValid &&
                isTitleValid &&
                isFirstNameValid &&
                isLastNameValid &&
                isDateOfBirthValid &&
                isPhoneNumberValid &&
                isMobileCodeValid &&
                isAddressValid
        );
    }, [
        emailValue,
        passwordValue,
        titleValue,
        firstNameValue,
        lastNameValue,
        dateOfBirthValue,
        phoneNumberValue,
        mobileCodeValue,
        addressValue,
    ]);

    const handleSubmit = () => {
        registerBookingWhizzMember(
            activePropertyId,
            emailValue,
            passwordValue,
            titleValue,
            firstNameValue,
            lastNameValue,
            dateOfBirthValue,
            phoneNumberValue,
            mobileCodeValue,
            countryCodeValue,
            addressValue
        ).then((success) => {
            if (success) {
                setIsRegisteredSuccessfully(true);
            } else {
                setHasRegisterFailed(true);
            }
        });
    };

    return (
        <Fragment>
            {isRegisteredSuccessfully && (
                <div className="w-full p-2 rounded border-1 border-green-100 bg-green-100/10 mb-4 text-sm">
                    <h2 className="font-bold">{t("register.successMessage.title")}</h2>
                    <p>{t("register.successMessage.message")}</p>
                </div>
            )}
            {!isRegisteredSuccessfully && (
                <FormProvider {...formMethods}>
                    <div className="text-left mb-3">
                        <Label>{t("register.email.label")}</Label>

                        <ControlledFormField
                            name="email"
                            type="email"
                            placeholder={t("register.email.label")}
                            rules={{
                                required: t("register.email.errorRequired"),
                                pattern: {
                                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message: t("register.email.errorInvalid"),
                                },
                            }}
                            onChange={(e) => setEmailValue(e.currentTarget.value)}
                            component={Input}
                            autoComplete="email"
                        />
                    </div>

                    <div className="text-left mb-3">
                        <Label>{t("register.password.label")}</Label>

                        <ControlledFormField
                            name="password"
                            type="password"
                            placeholder={t("register.password.label")}
                            rules={{
                                required: t("register.password.errorRequired"),
                                validate: (value: string) =>
                                    checkPasswordValidity(value) || (t("register.password.errorValidation") as string),
                            }}
                            onChange={(e) => setPasswordValue(e.currentTarget.value)}
                            component={Input}
                            autoComplete="password"
                        />

                        <ul className="mt-2 text-sm text-gray-500">
                            <li>{t("register.password.requirements.length")}</li>
                            <li>{t("register.password.requirements.cases")}</li>
                            <li>{t("register.password.requirements.number")}</li>
                            <li>{t("register.password.requirements.special")}</li>
                        </ul>
                    </div>

                    <div className="text-left mb-3">
                        <Label>{t("register.title.label")}</Label> <br />
                        <div className="w-full flex gap-3">
                            <div>
                                <input
                                    id="booking-whizz-title-input-0"
                                    className="mr-1"
                                    name="title"
                                    type="radio"
                                    value="Mr"
                                    checked={titleValue === "Mr"}
                                    onChange={(e) => setTitleValue(e.currentTarget.value)}
                                />
                                <Label className="mb-0" htmlFor="booking-whizz-title-input-0">
                                    {t("register.title.options.mr")}
                                </Label>
                            </div>

                            <div>
                                <input
                                    id="booking-whizz-title-input-1"
                                    className="mr-1"
                                    name="title"
                                    type="radio"
                                    value="Mrs"
                                    checked={titleValue === "Mrs"}
                                    onChange={(e) => setTitleValue(e.currentTarget.value)}
                                />
                                <Label className="mb-0" htmlFor="booking-whizz-title-input-1">
                                    {t("register.title.options.mrs")}
                                </Label>
                            </div>

                            <div>
                                <input
                                    id="booking-whizz-title-input-2"
                                    className="mr-1"
                                    name="title"
                                    type="radio"
                                    value="Ms"
                                    checked={titleValue === "Ms"}
                                    onChange={(e) => setTitleValue(e.currentTarget.value)}
                                />
                                <Label className="mb-0" htmlFor="booking-whizz-title-input-2">
                                    {t("register.title.options.ms")}
                                </Label>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2 mb-3">
                        <div className="text-start">
                            <Label>{t("register.firstName.label")}</Label>
                            <ControlledFormField
                                name="firstName"
                                placeholder={t("register.firstName.label")}
                                rules={{
                                    required: t("register.firstName.errorRequired"),
                                    validate: (value: string) =>
                                        checkNameValidity(value) || (t("register.firstName.errorValidation") as string),
                                }}
                                onChange={(e) => setFirstNameValue(e.currentTarget.value)}
                                component={Input}
                                autoComplete="first-name"
                            />
                        </div>

                        <div className="text-start">
                            <Label>{t("register.lastName.label")}</Label>
                            <ControlledFormField
                                name="lastName"
                                placeholder={t("register.lastName.label")}
                                rules={{
                                    required: t("register.lastName.errorRequired"),
                                    validate: (value: string) =>
                                        checkNameValidity(value) || (t("register.lastName.errorValidation") as string),
                                }}
                                onChange={(e) => setLastNameValue(e.currentTarget.value)}
                                component={Input}
                                autoComplete="last-name"
                            />
                        </div>
                    </div>

                    <div className="text-left mb-3">
                        <Label>{t("register.dateOfBirth.label")}</Label>
                        <ControlledFormField
                            name="dateOfBirth"
                            type="date"
                            placeholder={t("register.dateOfBirth.label")}
                            rules={{
                                required: t("register.dateOfBirth.errorRequired"),
                            }}
                            onChange={(e) => setDateOfBirthValue(e.currentTarget.value)}
                            component={Input}
                            autoComplete="date-of-birth"
                        />
                    </div>

                    <div className="text-left mb-3">
                        <Label>{t("register.phoneNumber.label")}</Label>
                        <div className="flex flex-auto">
                            <div>
                                <Select onValueChange={(value) => setMobileCodeValue(value)}>
                                    <SelectTrigger className="rounded-r-[0]">
                                        <SelectValue placeholder={t("register.phoneNumber.dialingCode")} />
                                    </SelectTrigger>
                                    <SelectContent className="overflow-y-scroll">
                                        <SelectGroup className="overflow-y-scroll max-h-52" data-scrollable>
                                            {dialingCodesOptions.map((option) => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex-1">
                                <ControlledFormField
                                    className="rounded-l-[0] border-l-[0]"
                                    name="phoneNumber"
                                    placeholder={t("register.phoneNumber.label")}
                                    rules={{
                                        required: t("register.phoneNumber.errorRequired"),
                                    }}
                                    onChange={(e) => setPhoneNumberValue(e.currentTarget.value)}
                                    component={Input}
                                    autoComplete="phone-number"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="text-left mb-3">
                        <Label>{t("register.country.label")}</Label>
                        <ControlledFormField
                            name="countryCode"
                            placeholder={t("register.country.placeholder")}
                            countries={countries as ICountry[]}
                            onChange={(countryCode) => setCountryCodeValue(countryCode)}
                            component={CountrySelect}
                        />
                    </div>

                    <div className="text-left mb-6">
                        <Label>{t("register.address.label")}</Label>
                        <ControlledFormField
                            name="address"
                            placeholder={t("register.address.label")}
                            rules={{
                                required: t("register.address.errorRequired"),
                            }}
                            component={Input}
                            onChange={(e) => setAddressValue(e.currentTarget.value)}
                            autoComplete="address"
                        />
                    </div>

                    {hasRegisterFailed && (
                        <div className="w-full p-2 rounded border-1 border-red-100 bg-red-100/10 mt-3 text-sm">
                            <h2 className="font-bold">{t("register.failureMessage.title")}</h2>
                            <p>{t("register.failureMessage.message")}</p>
                        </div>
                    )}

                    <Button className="w-full mt-3" disabled={!formMethods.formState.isValid || !isAbleToSubmit} onClick={handleSubmit}>
                        {t("register.submit")}
                    </Button>
                </FormProvider>
            )}
        </Fragment>
    );
};
